/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useState } from 'react';

import { I18n } from '@coze-arch/i18n';
import { logger } from '@coze-arch/logger';
import { IconCozTeamFill } from '@coze-arch/coze-design/icons';
import { Avatar, Modal, Select, Toast } from '@coze-arch/coze-design';
import { DeveloperApi } from '@coze-arch/bot-api';
import { SpaceType } from '@coze-arch/bot-api/developer_api';
import { CustomError } from '@coze-arch/bot-error';
import { useSpaceStore } from '@coze-arch/bot-studio-store';

export const useDuplicateBotToSpace = (botId: string) => {
  const [visible, setVisible] = useState(false);
  const [targetSpaceId, setTargetSpaceId] = useState<string>();
  const [loading, setLoading] = useState(false);

  const spaces = useSpaceStore(state => state.spaceList);
  const currentSpaceId = useSpaceStore(state => state.space.id);

  const teamSpaces = spaces.filter(
    space =>
      !!space.id &&
      space.space_type === SpaceType.Team &&
      !space.hide_operation &&
      space.id !== currentSpaceId,
  );

  const open = () => setVisible(true);

  const close = () => {
    setVisible(false);
    setTargetSpaceId(undefined);
  };

  const handleCopy = async () => {
    if (!targetSpaceId) {
      return;
    }
    setLoading(true);
    try {
      await DeveloperApi.DuplicateDraftBot({
        space_id: targetSpaceId,
        bot_id: botId,
      });
      Toast.success({
        content: I18n.t('bot_copy_success'),
        showClose: false,
      });
      close();
    } catch {
      logger.error({
        error: new CustomError(
          'copy bot to space',
          'copy bot to space error',
        ),
      });
    } finally {
      setLoading(false);
    }
  };

  const contextHolder = (
    <Modal
      visible={visible}
      title={I18n.t('bot_copy_to_other_space')}
      okText={I18n.t('Confirm')}
      cancelText={I18n.t('Cancel')}
      okButtonProps={{ loading, disabled: !targetSpaceId }}
      maskClosable={false}
      onOk={handleCopy}
      onCancel={close}
    >
      <Select
        className="w-full"
        value={targetSpaceId}
        placeholder={I18n.t('select_team')}
        onChange={val => setTargetSpaceId(val as string)}
      >
        {teamSpaces.map(space => (
          <Select.Option key={space.id} value={space.id}>
            {space.icon_url ? (
              <Avatar size="extra-small" src={space.icon_url} />
            ) : (
              <IconCozTeamFill />
            )}
            <span className="ml-[8px]">{space.name}</span>
          </Select.Option>
        ))}
      </Select>
    </Modal>
  );

  return { contextHolder, open, close, teamSpaces };
};
